import * as THREE from "three";
import TexturedGeoMesh from "./TexturedGeoMesh";

/**
 * Creates a photocube with the given image
 * @example
 * let photoCube = new PhotoCube(image);
 * scene.add(photoCube);
 */
export default class PhotoCube extends TexturedGeoMesh {
  raycaster;
  imageFov;

  /**
   * Creates a photosphere based on the given URL
   *
   * @param player
   * @param {object} image - image object containing full and optional low url
   * @param loadedCallback
   * @param promiseCallback
   * @param stereo
   * @param lowRes
   */
  constructor(
    player,
    image,
    loadedCallback = () => {},
    promiseCallback = () => {},
    stereo,
    lowRes = true,
  ) {
    const width = 1200;
    const geometry = new THREE.BoxBufferGeometry(width, width, width);

    super(player, geometry, "cube", () => {}, 90);

    this.imageFov = image.fov ?? null;
    this.raycaster = new THREE.Raycaster();

    this.image = image;
    this.stereo = stereo;
    this.scale.x = -1;

    let prom = this.loadTexture(image, false, loadedCallback, stereo, lowRes);
    promiseCallback(
      prom.then(() => {
        this.setupControls();
      }),
    );
  }

  setupControls() {
    if (this.player) {
      this.player.controlsDefault();
      this.player.resetControls(false);

      // this.player.controlsOrbit.maxPolarAngle = Infinity;
      // this.player.controlsOrbit.minPolarAngle = -Infinity;
    }
  }

  getScreenFace() {
    let camera = this.player.getActiveCamera();

    if (camera) {
      this.updateCameraCoords();

      this.raycaster.ray.origin.copy(this.cameraPosition);
      this.raycaster.ray.direction
        .subVectors(this.position, camera.position)
        .normalize();

      let intersects = this.raycaster.intersectObject(this, true);

      if (intersects.length > 0) {
        return this.material[intersects[0].face.materialIndex].name;
      }
    }

    return null;
  }

  /**
   *
   * @param texture
   * @param face
   * @param full
   */
  setTexture(texture, face = null, full = false) {
    let key = face;
    if (
      texture &&
      "materials" in this &&
      this.materials !== undefined &&
      key in this.materials
    ) {
      if (
        !(key in this.fullyLoadedMaterials) ||
        this.fullyLoadedMaterials[key] !== true ||
        full === true
      ) {
        this.disposeMaterial(this.materials[key]);

        this.materials[key].map = texture;
        this.materials[key].side = THREE.DoubleSide;

        if (this.player !== undefined && this.player.renderer !== undefined) {
          this.materials[key].map.anisotropy =
            this.player.renderer.capabilities.getMaxAnisotropy();
          this.materials[key].map.minFilter = this.materials[
            key
          ].map.magFilter = THREE.LinearFilter;
        }

        if (this.onTextureSet) {
          this.onTextureSet(texture, face, full);
        }
      }

      if (full) {
        this.fullyLoadedMaterials[face] = true;
      }

      this.realBoundingBox.setFromObject(this);
    } else {
      if ((this.materials[key] ?? undefined) === undefined) {
        console.warn("Could not set texture, material is undefined");
      } else {
        throw Error("Could not set texture");
      }
    }
  }

  /**
   *
   * @param image
   * @param flip
   * @param callback
   * @param stereo
   * @param lowRes
   * @param resolution
   * @returns {Promise<unknown>}
   */
  async getTexture(
    image,
    flip,
    callback = () => {},
    stereo,
    lowRes,
    resolution,
  ) {
    return await new Promise((resolve, reject) => {
      this.player.textureManager.fetch({
        image,
        flip,
        callback: {
          onLoaded: ({ texture, full, res }) => {
            callback(texture, full, res);
            resolve({ texture, full, res });
          },
          setTexture: (texture, face, full) => {
            this.setTexture(texture, face, full);
          },
          getPriorityFace: () => {
            return this.getScreenFace();
          },
        },
        nonCritical: false,
        stereo,
        lowRes,
        resolution,
        isThumbnail: this.player.isThumbnail,
      });
    });
  }

  /**
   * Loads texture onto mesh material from url
   *
   * @param {object} image
   * @param {boolean} flip - whether to flip the texture
   * @param callback
   * @param stereo
   * @param lowRes
   */
  loadTexture(image, flip, callback = () => {}, stereo, lowRes) {
    return new Promise((resolve, reject) => {
      try {
        this.getTexture(
          image,
          flip,
          (texture, full, res) => {
            callback(texture, full, res);
            resolve({ texture, full, res });
          },
          stereo,
          lowRes,
          undefined,
        );
      } catch (e) {
        console.log(e);
        callback(null, false, null);
        reject();
      }
    });
  }

  afterTransition() {
    this.setupControls();
  }

  /**
   *
   * @returns {Mesh}
   */
  projectSphere(copyRotation = false) {
    return super.projectSphere(true);
  }
}
